// Jazyk uživatelského prostředí (ne dokumentu — ten nese `offer.lang`).
// Záměrně bez knihovny: dva jazyky, plochý slovník, typová kontrola klíčů přes `typeof cs`.

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Lang } from '@shared/types';

const cs = {
  'common.loading': 'Načítám…',
  'common.saving': 'Ukládám…',
  'common.save': 'Uložit',
  'common.delete': 'Smazat',
  'common.remove': 'Odebrat',
  'common.add': 'Přidat',
  'common.close': 'Zavřít',
  'common.phone': 'Telefon',
  'common.email': 'E-mail',
  'common.vatRatePct': 'Sazba DPH (%)',
  'common.paymentTerms': 'Platební podmínky',
  'common.title': 'Nadpis',
  'common.text': 'Text',
  'common.lead': 'Úvodní odstavec',
  'common.langCs': 'Čeština',
  'common.langEn': 'English',

  'nav.offers': 'Nabídky',
  'nav.catalog': 'Katalog',
  'nav.settings': 'Nastavení',
  'nav.users': 'Uživatelé',
  'nav.logout': 'Odhlásit se',
  'nav.uiLang': 'Jazyk prostředí',

  'login.intro': 'Generátor klientských nabídek. Přihlášení přes Google Workspace účet',
  'login.opening': 'Otevírám Google…',
  'login.signIn': 'Přihlásit se přes Google',
  'login.failed': 'Přihlášení selhalo',
  'login.gisMissing': 'Google Identity Services se nenačetly',
  'login.help': 'Pokud se ti nepovede přihlásit, kontaktuj admina v kanceláři.',

  'offers.title': 'Nabídky',
  'offers.search': 'Hledat klienta nebo číslo…',
  'offers.creating': 'Zakládám…',
  'offers.new': 'Nová nabídka',
  'offers.empty': 'Zatím žádné nabídky',
  'offers.emptyHint': 'Založ první nabídku, vyber služby z katalogu a vygeneruj PDF.',
  'offers.notFound': 'Nic nenalezeno',
  'offers.notFoundHint': 'Zkus jiný dotaz.',
  'offers.colNumber': 'Číslo',
  'offers.colClient': 'Klient',
  'offers.colProperty': 'Nemovitost',
  'offers.colDate': 'Datum',
  'offers.colStatus': 'Stav',
  'offers.colTotal': 'Celkem s DPH',

  'status.draft': 'Rozpracovaná',
  'status.sent': 'Odeslaná',
  'status.accepted': 'Přijatá',
  'status.archived': 'Archivovaná',

  'size.flat': 'Jednotná cena',
  'size.studio': 'Studio',
  'size.1br': '1 ložnice',
  'size.2br': '2 ložnice',
  'size.3br': '3 ložnice',

  'category.ownership': 'Vlastnictví a správa',
  'category.visa': 'Vízové služby',
  'category.banking': 'Bankovnictví',
  'category.legal': 'Právní a administrativní',
  'category.resale': 'Preprodej',
  'category.other': 'Ostatní',

  'editor.notFound': 'Nabídka nenalezena.',
  'editor.newOffer': 'Nová nabídka',
  'editor.saving': 'ukládám…',
  'editor.saved': 'uloženo',
  'editor.duplicate': 'Duplikovat',
  'editor.confirmDelete': 'Opravdu smazat tuto nabídku?',
  'editor.print': 'Tisk',
  'editor.pdfFallback':
    'dokument můžeš zatím vytisknout tlačítkem „Tisk" (v dialogu zvol Uložit jako PDF).',
  'editor.pdfFailed': 'Generování PDF selhalo ({status})',
  'editor.clientSection': 'Klient a nemovitost',
  'editor.client': 'Klient',
  'editor.property': 'Nemovitost',
  'editor.unit': 'Jednotka',
  'editor.developer': 'Developer',
  'editor.offerDate': 'Datum nabídky',
  'editor.validUntil': 'Platnost do',
  'editor.docLang': 'Jazyk dokumentu',
  'editor.langSwitchNote': 'Přepnutí jazyka přeloží jen pevné části. Obsah služeb je',
  'editor.langSwitchNoteCs': 'v jazyce, který jsi zvolil při vkládání',
  'editor.langSwitchNoteEn': 'nutné přeložit zvlášť',
  'editor.translateTo.cs': 'Přeložit obsah do češtiny',
  'editor.translateTo.en': 'Přeložit obsah do angličtiny',
  'editor.confirmTranslate.cs': 'Přeložit obsah všech služeb do češtiny? Přepíše to jejich současné texty.',
  'editor.confirmTranslate.en':
    'Přeložit obsah všech služeb do angličtiny? Přepíše to jejich současné texty.',
  'editor.translated': 'Přeloženo. Z katalogu: {catalog} · strojově: {machine}.',
  'editor.priceDisplay': 'Zobrazení cen v dokumentu',
  'editor.priceDetailed': 'Detailní rozpis — cena u každé služby',
  'editor.priceSummary': 'Jen souhrnná cena — jedna částka na konci',
  'editor.priceSummaryHint': 'U služeb se ceny netisknou, v kalkulaci je jen celková částka včetně DPH.',
  'editor.priceDetailedHint': 'Cena u každé služby a plný rozpis včetně DPH v kalkulaci.',
  'editor.termsSection': 'Podmínky a kontakt',
  'editor.notes': 'Interní poznámka (netiskne se)',
  'editor.services': 'Služby',
  'editor.addService': 'Přidat službu',
  'editor.noServices': 'Zatím žádné služby. Přidej první z katalogu.',
  'editor.subtotal': 'Celkem bez DPH',
  'editor.vat': 'DPH {rate} %',
  'editor.total': 'Celkem k úhradě',
  'editor.dragHint': 'Přetáhni pro změnu pořadí (nebo šipky ↑ ↓ na klávesnici)',
  'editor.moveUp': 'Posunout nahoru',
  'editor.moveDown': 'Posunout dolů',
  'editor.noVat': 'bez DPH',
  'editor.noDetail': 'bez detailu',
  'editor.itemTitle': 'Název v dokumentu',
  'editor.itemPrice': 'Cena (AED bez DPH)',
  'editor.itemQty': 'Množství',
  'editor.itemSubtitle': 'Popisek u ceny',
  'editor.itemIncludes': 'Co je v ceně (jedna odrážka na řádek)',
  'editor.itemConditions': 'Podmínky (jedna odrážka na řádek)',
  'editor.itemIsFrom': 'Cena „od"',
  'editor.itemVat': 'Podléhá DPH',
  'editor.itemOwnPage': 'Vlastní stránka',

  'preview.title': 'Náhled nabídky',
  'preview.overflow':
    'Obsah přetéká na další stranu (strana {pages}). Zkrať texty, nebo to nech být — dokument se jen zalomí, nic se neořízne.',

  'picker.search': 'Hledat službu…',
  'picker.loading': 'Načítám katalog…',
  'picker.empty': 'Katalog je prázdný nebo dotazu nic neodpovídá.',
  'picker.addNoPrice': 'Přidat bez ceny',

  'services.title': 'Katalog služeb',
  'services.new': 'Nová služba',
  'services.empty': 'Katalog je prázdný. Přidej první službu.',
  'services.hidden': 'skrytá',
  'services.selectHint': 'Vyber službu vlevo, nebo přidej novou.',
  'services.key': 'Klíč (interní, bez diakritiky)',
  'services.category': 'Kategorie',
  'services.order': 'Pořadí',
  'services.sizeTiers': 'Ceny podle velikosti jednotky',
  'services.active': 'Nabízet při výběru',
  'services.vatRate': 'Sazba DPH',
  'services.name': 'Název',
  'services.eyebrow': 'Štítek nad nadpisem',
  'services.eyebrowPlaceholder': 'Inspekce',
  'services.includes': 'Co je v ceně (odrážky, jedna na řádek)',
  'services.conditions': 'Podmínky (odrážky, jedna na řádek)',
  'services.prices': 'Ceny',
  'services.addPrice': 'Přidat cenu',
  'services.from': '„od"',
  'services.sublabelCs': 'Popisek CZ',
  'services.sublabelEn': 'Popisek EN',
  'services.removePrice': 'Odebrat cenu',
  'services.create': 'Vytvořit službu',
  'services.saveChanges': 'Uložit změny',
  'services.confirmDelete': 'Smazat službu z katalogu? Hotové nabídky zůstanou nedotčené.',
  'services.keyTaken': 'Služba s tímto klíčem už existuje',

  'settings.title': 'Nastavení',
  'settings.contentLang': 'Texty dokumentu v jazyce:',
  'settings.saveFailed': 'Uložení selhalo',
  'settings.saved': 'Uloženo. Nové nabídky i náhledy už používají aktuální texty.',
  'settings.defaults': 'Výchozí hodnoty nové nabídky',
  'settings.validityDays': 'Platnost (dní)',
  'settings.cover': 'Titulní strana',
  'settings.slogan': 'Slogan',
  'settings.coverTitle': 'Nadpis (<br> zalomí)',
  'settings.byline': 'Podpis pod nadpisem',
  'settings.whyUs': 'Strana „Proč BuyDubai"',
  'settings.eyebrow': 'Štítek',
  'settings.points': 'Body (dlaždice)',
  'settings.calloutTitle': 'Callout — nadpis',
  'settings.calloutBody': 'Callout — text',
  'settings.summaryConditions': 'Podmínky v souhrnu',
  'settings.summaryConditionsHint':
    'Odrážky, jedna na řádek. Značky {tags} se nahradí hodnotami z nabídky.',
  'settings.nextSteps': 'Strana „Jak budeme pokračovat"',
  'settings.steps': 'Kroky',
  'settings.contactTitle': 'Kontakt — nadpis',
  'settings.contactBody': 'Kontakt — text',

  'users.title': 'Uživatelé',
  'users.name': 'Jméno',
  'users.role': 'Role',
  'users.access': 'Přístup',
  'users.me': '(ty)',
  'users.roleUser': 'Uživatel',
  'users.roleAdmin': 'Admin',
  'users.active': 'Aktivní',
  'users.blocked': 'Zablokován',
  'users.hint':
    'Přihlásit se může kdokoli z domény buydubai.eu — rovnou jako uživatel, který tvoří nabídky. Admin navíc spravuje katalog, nastavení a uživatele.',
};

export type MessageKey = keyof typeof cs;

const en: Record<MessageKey, string> = {
  'common.loading': 'Loading…',
  'common.saving': 'Saving…',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.remove': 'Remove',
  'common.add': 'Add',
  'common.close': 'Close',
  'common.phone': 'Phone',
  'common.email': 'Email',
  'common.vatRatePct': 'VAT rate (%)',
  'common.paymentTerms': 'Payment terms',
  'common.title': 'Title',
  'common.text': 'Text',
  'common.lead': 'Intro paragraph',
  'common.langCs': 'Čeština',
  'common.langEn': 'English',

  'nav.offers': 'Proposals',
  'nav.catalog': 'Catalogue',
  'nav.settings': 'Settings',
  'nav.users': 'Users',
  'nav.logout': 'Sign out',
  'nav.uiLang': 'Interface language',

  'login.intro': 'Client proposal generator. Sign in with your Google Workspace account',
  'login.opening': 'Opening Google…',
  'login.signIn': 'Sign in with Google',
  'login.failed': 'Sign-in failed',
  'login.gisMissing': 'Google Identity Services failed to load',
  'login.help': "If you can't sign in, contact the admin at the office.",

  'offers.title': 'Proposals',
  'offers.search': 'Search client or number…',
  'offers.creating': 'Creating…',
  'offers.new': 'New proposal',
  'offers.empty': 'No proposals yet',
  'offers.emptyHint': 'Create your first proposal, pick services from the catalogue and generate a PDF.',
  'offers.notFound': 'Nothing found',
  'offers.notFoundHint': 'Try a different search.',
  'offers.colNumber': 'Number',
  'offers.colClient': 'Client',
  'offers.colProperty': 'Property',
  'offers.colDate': 'Date',
  'offers.colStatus': 'Status',
  'offers.colTotal': 'Total incl. VAT',

  'status.draft': 'Draft',
  'status.sent': 'Sent',
  'status.accepted': 'Accepted',
  'status.archived': 'Archived',

  'size.flat': 'Flat price',
  'size.studio': 'Studio',
  'size.1br': '1 bedroom',
  'size.2br': '2 bedrooms',
  'size.3br': '3 bedrooms',

  'category.ownership': 'Ownership & management',
  'category.visa': 'Visa services',
  'category.banking': 'Banking',
  'category.legal': 'Legal & administrative',
  'category.resale': 'Resale',
  'category.other': 'Other',

  'editor.notFound': 'Proposal not found.',
  'editor.newOffer': 'New proposal',
  'editor.saving': 'saving…',
  'editor.saved': 'saved',
  'editor.duplicate': 'Duplicate',
  'editor.confirmDelete': 'Really delete this proposal?',
  'editor.print': 'Print',
  'editor.pdfFallback': 'you can print the document with the "Print" button for now (choose Save as PDF).',
  'editor.pdfFailed': 'PDF generation failed ({status})',
  'editor.clientSection': 'Client & property',
  'editor.client': 'Client',
  'editor.property': 'Property',
  'editor.unit': 'Unit',
  'editor.developer': 'Developer',
  'editor.offerDate': 'Proposal date',
  'editor.validUntil': 'Valid until',
  'editor.docLang': 'Document language',
  'editor.langSwitchNote': 'Switching the language only translates the fixed parts. Service content is',
  'editor.langSwitchNoteCs': 'in the language you chose when adding it',
  'editor.langSwitchNoteEn': 'translated separately',
  'editor.translateTo.cs': 'Translate content to Czech',
  'editor.translateTo.en': 'Translate content to English',
  'editor.confirmTranslate.cs': 'Translate all services to Czech? This overwrites their current texts.',
  'editor.confirmTranslate.en': 'Translate all services to English? This overwrites their current texts.',
  'editor.translated': 'Translated. From catalogue: {catalog} · machine: {machine}.',
  'editor.priceDisplay': 'Price display in the document',
  'editor.priceDetailed': 'Detailed — price for each service',
  'editor.priceSummary': 'Summary only — one amount at the end',
  'editor.priceSummaryHint': 'No prices next to services; the calculation shows only the total incl. VAT.',
  'editor.priceDetailedHint': 'Price for each service and a full breakdown incl. VAT in the calculation.',
  'editor.termsSection': 'Terms & contact',
  'editor.notes': 'Internal note (not printed)',
  'editor.services': 'Services',
  'editor.addService': 'Add service',
  'editor.noServices': 'No services yet. Add the first one from the catalogue.',
  'editor.subtotal': 'Total excl. VAT',
  'editor.vat': 'VAT {rate} %',
  'editor.total': 'Total due',
  'editor.dragHint': 'Drag to reorder (or use the ↑ ↓ arrows)',
  'editor.moveUp': 'Move up',
  'editor.moveDown': 'Move down',
  'editor.noVat': 'no VAT',
  'editor.noDetail': 'no detail page',
  'editor.itemTitle': 'Title in document',
  'editor.itemPrice': 'Price (AED excl. VAT)',
  'editor.itemQty': 'Quantity',
  'editor.itemSubtitle': 'Price label',
  'editor.itemIncludes': "What's included (one bullet per line)",
  'editor.itemConditions': 'Conditions (one bullet per line)',
  'editor.itemIsFrom': '"From" price',
  'editor.itemVat': 'Subject to VAT',
  'editor.itemOwnPage': 'Own page',

  'preview.title': 'Proposal preview',
  'preview.overflow':
    'Content overflows onto the next page (page {pages}). Shorten the texts or leave it — the document just breaks, nothing is cut off.',

  'picker.search': 'Search services…',
  'picker.loading': 'Loading catalogue…',
  'picker.empty': 'The catalogue is empty or nothing matches.',
  'picker.addNoPrice': 'Add without price',

  'services.title': 'Service catalogue',
  'services.new': 'New service',
  'services.empty': 'The catalogue is empty. Add the first service.',
  'services.hidden': 'hidden',
  'services.selectHint': 'Select a service on the left, or add a new one.',
  'services.key': 'Key (internal, ASCII only)',
  'services.category': 'Category',
  'services.order': 'Order',
  'services.sizeTiers': 'Prices by unit size',
  'services.active': 'Offer in picker',
  'services.vatRate': 'VAT rate',
  'services.name': 'Name',
  'services.eyebrow': 'Eyebrow above title',
  'services.eyebrowPlaceholder': 'Inspection',
  'services.includes': "What's included (bullets, one per line)",
  'services.conditions': 'Conditions (bullets, one per line)',
  'services.prices': 'Prices',
  'services.addPrice': 'Add price',
  'services.from': '"from"',
  'services.sublabelCs': 'Label CZ',
  'services.sublabelEn': 'Label EN',
  'services.removePrice': 'Remove price',
  'services.create': 'Create service',
  'services.saveChanges': 'Save changes',
  'services.confirmDelete': 'Delete the service from the catalogue? Existing proposals stay untouched.',
  'services.keyTaken': 'A service with this key already exists',

  'settings.title': 'Settings',
  'settings.contentLang': 'Document texts in:',
  'settings.saveFailed': 'Saving failed',
  'settings.saved': 'Saved. New proposals and previews now use the current texts.',
  'settings.defaults': 'New proposal defaults',
  'settings.validityDays': 'Validity (days)',
  'settings.cover': 'Cover page',
  'settings.slogan': 'Slogan',
  'settings.coverTitle': 'Title (<br> breaks line)',
  'settings.byline': 'Byline under title',
  'settings.whyUs': '"Why BuyDubai" page',
  'settings.eyebrow': 'Eyebrow',
  'settings.points': 'Points (tiles)',
  'settings.calloutTitle': 'Callout — title',
  'settings.calloutBody': 'Callout — text',
  'settings.summaryConditions': 'Conditions in summary',
  'settings.summaryConditionsHint':
    'Bullets, one per line. Tags {tags} are replaced with values from the proposal.',
  'settings.nextSteps': '"Next steps" page',
  'settings.steps': 'Steps',
  'settings.contactTitle': 'Contact — title',
  'settings.contactBody': 'Contact — text',

  'users.title': 'Users',
  'users.name': 'Name',
  'users.role': 'Role',
  'users.access': 'Access',
  'users.me': '(you)',
  'users.roleUser': 'User',
  'users.roleAdmin': 'Admin',
  'users.active': 'Active',
  'users.blocked': 'Blocked',
  'users.hint':
    'Anyone from the buydubai.eu domain can sign in — directly as a user who creates proposals. Admins also manage the catalogue, settings and users.',
};

const dictionaries: Record<Lang, Record<MessageKey, string>> = { cs, en };

export type TFunction = (key: MessageKey, vars?: Record<string, string | number>) => string;

export function translate(lang: Lang, key: MessageKey, vars?: Record<string, string | number>): string {
  const text = dictionaries[lang][key] ?? cs[key] ?? key;
  return vars ? text.replace(/\{(\w+)\}/g, (_, name) => String(vars[name] ?? `{${name}}`)) : text;
}

const STORAGE_KEY = 'ui-lang';

function initialLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'cs' || stored === 'en') return stored;
  } catch {
    // localStorage nemusí být dostupný (privátní režim) — spadneme na prohlížeč.
  }
  return navigator.language?.toLowerCase().startsWith('cs') ? 'cs' : 'en';
}

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignorujeme — volba vydrží aspoň do reloadu
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ lang, setLang, t: (key, vars) => translate(lang, key, vars) }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n musí být uvnitř <I18nProvider>');
  return ctx;
}

/** Přepínač CZ / EN — v hlavičce i na přihlašovací stránce. */
export function LangSwitch({ className = '' }: { className?: string }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div
      className={`flex gap-0.5 rounded-md border border-gray-200 p-0.5 text-xs ${className}`}
      role="group"
      aria-label={t('nav.uiLang')}
      title={t('nav.uiLang')}
    >
      {(['cs', 'en'] as Lang[]).map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded px-2 py-1 font-semibold uppercase transition-colors ${
            lang === l ? 'bg-teal text-white' : 'text-gray-500 hover:text-teal'
          }`}
        >
          {l === 'cs' ? 'CZ' : 'EN'}
        </button>
      ))}
    </div>
  );
}
